<script lang="ts">
  /**
   * MemorySwitch — ticket #9 (2026-10-05, the Chair: "fix the MU issues"): a person views their AGENTS' memory in MU.
   *
   * An agent keeps its own memory (its own NEXUS tenant). A sponsor holds a READ grant on each agent's tenant
   * (vault_nexus_grants — sponsorship is provenance, so the view is a grant, revocable); mu-api lists them
   * (GET /tenants/viewable) and serves reads of a viewed tenant, billed to the viewer, writes refused.
   *
   * Shown only when there is something to switch to. Switching: every call carries the viewed tenant (api.svelte.ts),
   * the constellation reloads (Constellation's effect), the task graph too when it's showing; writing is locked and a
   * READ-ONLY chip names whose memory this is.
   */
  import { mco } from './mcoAuth.svelte'
  import { fetchViewable } from './api.svelte'
  import { writeMode, selection } from './selection.svelte'
  import { appView, loadTaskGraph } from './taskGraphStore.svelte'

  /* the list follows the sign-in (and its tenant) */
  $effect(() => {
    void mco.token
    void mco.tenant?.id
    if (mco.token) void fetchViewable()
  })

  function pick(e: Event): void {
    const id = Number((e.currentTarget as HTMLSelectElement).value)
    const next = mco.viewable.find((t) => t.id === id) ?? null
    if ((next?.id ?? null) === (mco.viewing?.id ?? null)) return
    mco.viewing = next
    // read-only while viewing: no write arms, no write dialogs, no stale selection from the other memory
    if (next) { writeMode.unlocked = false; writeMode.addDialogOpen = false; writeMode.ingestDialogOpen = false }
    selection.selectedId = null
    if (appView.mode === 'tasks') void loadTaskGraph()
  }
  const own = $derived(mco.own ? `My memory · ${mco.own.name}` : 'My memory')
</script>

{#if mco.viewable.length > 0}
  <span class="ms" class:viewing={!!mco.viewing}>
    <select class="ms-sel" value={mco.viewing?.id ?? 0} onchange={pick} title="Whose memory you are looking at — your own, or an agent's you sponsor (read-only)">
      <option value={0}>{own}</option>
      {#each mco.viewable as t (t.id)}
        <option value={t.id}>{t.name}</option>
      {/each}
    </select>
    {#if mco.viewing}<span class="ms-chip" title="Viewing your agent's memory — read-only">READ-ONLY</span>{/if}
  </span>
{/if}

<style>
  .ms { display: inline-flex; align-items: center; gap: var(--s-2, 6px); }
  .ms-sel {
    background: var(--surface, #0d1117);
    color: var(--on-surface, #e6edf3);
    border: 1px solid var(--outline-variant, #30363d);
    font: inherit;
    font-size: 11px;
    letter-spacing: 0.06em;
    padding: 2px 6px;
    max-width: 220px;
  }
  .ms.viewing .ms-sel { border-color: var(--accent, #e3b341); }
  .ms-chip {
    font-size: 10px;
    letter-spacing: 0.12em;
    padding: 1px 6px;
    border: 1px solid var(--accent, #e3b341);
    color: var(--accent, #e3b341);
  }
</style>
